import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import APIService from '../APIService';
import { EntityTypes } from '../../constants';
import { type InvitePayload } from '../../models/Org';
import type { DashboardFilters } from '../../types';
import { environment } from '../../environments/environment';
import { useNavigate, useParams } from 'react-router-dom';
import { message } from 'antd';
import { getAliasFromType, getTypeFromAlias } from '../../utils/Utils';

interface UseEntityParams {
  entityId: string;
  entityType: string;
}

const useEntity = ({ entityId, entityType }: UseEntityParams) => {
  return useQuery({
    queryKey: ['entity', entityId, entityType],
    queryFn: async () => {
      const entity = await APIService.GET(`/${entityType}/${entityId}`);
      return entity.data;
    },
    enabled: !!entityId && !!entityType,
  });
};

const useCurrentEntity = () => {
  const { entityId = '', entityType = '' } = useParams();
  return useEntity({ entityId, entityType: getTypeFromAlias(entityType) });
};

const useInbox = (inboxId: string) => {
  const results = useEntity({ entityId: inboxId, entityType: EntityTypes.BOARD });
  return results;
};

// Right now the optimistic update code is here, but it might be necessary to have it passed as a parameter in the future.
const useBlockUsers = ({ entityId, entityType, queryKey }: UseEntityParams & { queryKey: unknown[] }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (blockedUsers: Array<{ username: string; type: string }>) => {
      const res = await APIService.POST(`/${entityType}/${entityId}/blockuser`, undefined, { block: blockedUsers });
      return res.data;
    },
    onMutate: async (blockedUsers: Array<{ username: string; type: string }>) => {
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, (old: any) => {
        return {
          ...old,
          pages: old?.pages?.map((page) => {
            return {
              ...page,
              hits: page.hits.filter(
                (post) =>
                  !blockedUsers.some(
                    (blockedUser) => blockedUser.username === post.username && blockedUser.type === post.type,
                  ),
              ),
            };
          }),
        };
      });
      return { previousData };
    },
    onSuccess: () => {
      message.success('User successfully blocked.');
    },
    onError: (_, blockedUsers, context) => {
      message.error('User could not be blocked. Please try again.');
      queryClient.setQueryData(queryKey, context?.previousData);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey });
    },
  });
};

const useBlockedUsers = (inboxId: string) => {
  const { data } = useInbox(inboxId);

  if (data?.blocked) {
    return Object.keys(data.blocked).flatMap((socialService) =>
      data.blocked[socialService].map((username: string) => ({
        socialService,
        username,
      })),
    );
  }

  return [];
};

const useUnblockUser = (inboxId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (unblockedUser: { username: string; socialService: string }) => {
      const res = await APIService.POST(`/board/${inboxId}/unblockuser`, undefined, {
        type: unblockedUser.socialService,
        username: unblockedUser.username,
      });
      return res.data;
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['entity', inboxId] });
    },
  });
};

const useUserEntities = (orgId: string = '') => {
  return useQuery({
    queryKey: ['userEntities', orgId],
    queryFn: async () => {
      const entities = await APIService.GET(`/user/entities/${orgId}`);
      return entities.data;
    },
    enabled: !!orgId,
  });
};

const useEntityMembers = ({ entityId, entityType }: UseEntityParams) => {
  return useQuery({
    queryKey: ['entityMembers', entityId, entityType],
    queryFn: async () => {
      const entity = await APIService.GET(`/${entityType}/${entityId}/members`);
      return entity.data;
    },
    enabled: !!entityId && !!entityType,
  });
};

const useEntityMember = ({ entityId, entityType, userId }: UseEntityParams & { userId: string }) => {
  const { data: members } = useEntityMembers({ entityId, entityType });
  return members?.find((member) => member._id === userId);
};

const useEntityInvite = ({ entityId, entityType, orgId }: UseEntityParams & { orgId?: string }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: InvitePayload) => {
      const formattedPayload = {
        invitees: payload,
        userPoolId: environment.userPoolId,
        orgId,
      };
      const res = await APIService.POST(`/${entityType}/${entityId}/members`, undefined, { ...formattedPayload });
      return res.data;
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['entityMembers', entityId] });
    },
  });
};

const useEntityMemberRoleUpdate = ({ entityId, entityType }: UseEntityParams) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { memberId: string; role: string }) => {
      const res = await APIService.POST(`/${entityType}/${entityId}/members/${payload.memberId}/role`, undefined, {
        ...payload,
      });
      return res.data;
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['entityMembers', entityId] });
    },
  });
};

const useRemoveEntityMember = ({ entityId, entityType }: UseEntityParams) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (memberId: string) => {
      const res = await APIService.DELETE(`/${entityType}/${entityId}/member/${memberId}`);
      return res.data;
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['entityMembers', entityId] });
    },
  });
};

const useEntityCreate = (orgId?: string, orgPath?: string, redirect?: boolean) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (payload: { entityType: string; name: string }) => {
      const res = await APIService.POST(`/${payload.entityType}`, undefined, { orgId, name: payload.name });
      return res.data;
    },
    onSuccess: (data) => {
      if (redirect) {
        navigate(`/${orgPath}/${getAliasFromType(data.entityType)}/${data._id}/settings/general`);
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['userEntities', orgId] });
    },
  });
};

const useEntityUpdate = ({ entityId, entityType }: UseEntityParams) => {
  return useMutation({
    mutationFn: async (updates: Record<string, any>) => {
      const res = await APIService.POST(`/${entityType}/${entityId}`, undefined, updates);
      return res.data;
    },
  });
};

const useEntityMetaUpdate = ({ entityId, entityType }: UseEntityParams) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (updates: Record<string, any>) => {
      const res = await APIService.POST(`/${entityType}/${entityId}/meta`, undefined, { metadata: updates });
      return res.data;
    },
    onMutate: async (updates: Record<string, any>) => {
      const previous = queryClient.getQueryData(['entity', entityId, entityType]);
      await queryClient.setQueryData(['entity', entityId, entityType], (old: any) => {
        return {
          ...old,
          metadata: {
            ...old.metadata,
            ...updates,
          },
        };
      });
      return { previous };
    },
    onError: async (_, updates, context) => {
      queryClient.setQueryData(['entity', entityId, entityType], context?.previous);
    },
  });
};

const useEntityDelete = ({ entityId, entityType }: UseEntityParams) => {
  return useMutation({
    mutationFn: async () => {
      const res = await APIService.DELETE(`/${entityType}/${entityId}`);
      return res.data;
    },
  });
};

const useEntityDuplicate = ({ entityId, entityType }: UseEntityParams) => {
  return useMutation({
    mutationFn: async (orgId: string) => {
      const res = await APIService.POST(`/${entityType}/${entityId}/duplicate`, undefined, { orgId });
      return res.data;
    },
  });
};

const useExportToBoard = ({ entityId, entityType }: UseEntityParams) => {
  return useMutation({
    mutationFn: async () => {
      const res = await APIService.POST(`/${entityType}/${entityId}/boards`);
      return res.data;
    },
  });
};

const useBlockWords = (inboxId: string, entityType: string = 'board') => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (blockedWords: string[]) => {
      const res = await APIService.POST(`/${entityType}/${inboxId}/blockwords`, undefined, { blockedWords });
      return res.data;
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['entity', inboxId] });
    },
  });
};

const useUnblockWord = (inboxId: string, entityType: string = 'board') => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (unblockedWord: string) => {
      const res = await APIService.POST(`/${entityType}/${inboxId}/unblockword`, undefined, { unblockedWord });
      return res.data;
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['entity', inboxId] });
    },
  });
};

const useToggleWordBlacklist = (inboxId: string, entityType: string = 'board') => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (active: boolean) => {
      const res = await APIService.POST(`/${entityType}/${inboxId}/wordblacklist`, undefined, { active });
      return res.data;
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['entity', inboxId] });
    },
  });
};

const useRequireWord = (inboxId: string, entityType: string = 'board') => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (requiredWords: string[]) => {
      const res = await APIService.POST(`/${entityType}/${inboxId}/required-words`, undefined, { requiredWords });
      return res.data;
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['entity', inboxId] });
    },
  });
};

const useRemoveRequiredWord = (inboxId: string, entityType: string = 'board') => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (word: string) => {
      const res = await APIService.DELETE(`/${entityType}/${inboxId}/required-words/${word}`);
      return res.data;
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['entity', inboxId] });
    },
  });
};

const useInboxAnalytics = ({ inboxId, filters }: { inboxId: string; filters: DashboardFilters }) => {
  return useQuery({
    queryKey: [inboxId, filters.start, filters.end, filters.interval, filters.platforms[0]],
    queryFn: async () => {
      const res = await APIService.POST(`/board/${inboxId}/dashboard`, undefined, { filters });
      return res.data;
    },
    initialData: {
      postScore: 0,
      customLinkCount: 0,
      engagementCount: 0,
      postsPerTimePeriod: {
        all: [],
        platforms: [],
      },
      relatedTags: [],
      userData: [],
    },
  });
};

const useInboxBoardsArray = ({ entityId }: { entityId: string }) => {
  const { data: entity, isPending } = useEntity({ entityId, entityType: EntityTypes.BOARD });
  if (isPending) return [];
  return Object.values(entity?.folders);
};

const useEntityFolder = ({ entityId, entityType, folderId }: { entityId: string; entityType: string; folderId: string }) => {
  const { data: entity, isPending } = useEntity({ entityId, entityType });
  if (isPending) return null;
  return entity?.folders[folderId];
};

const useRemoveInboxAsset = ({ entityId, entityType }: UseEntityParams) => {
  return useMutation({
    mutationFn: async (assetName: string) => {
      const res = await APIService.DELETE(`/${entityType}/${entityId}/assets/${assetName}`);
      return res.data;
    },
  });
};

const usePublishedFoldersCount = (orgId: string = '') => {
  const { data: entities = [], ...helpers } = useUserEntities(orgId);
  const boards = entities.filter((entity) => entity.type === EntityTypes.BOARD);
  const currentTotalPublished = boards.reduce((count, board) => {
    for (const folder in board.folders) {
      if (board?.folders[folder].published) {
        count += 1;
      }
    }
    return count;
  }, 0);

  return { data: currentTotalPublished, ...helpers };
};

export {
  useEntityMembers,
  useEntityMember,
  useEntityInvite,
  useEntityMemberRoleUpdate,
  useRemoveEntityMember,
  useUserEntities,
  useInbox,
  useBlockUsers,
  useBlockedUsers,
  useUnblockUser,
  useEntityMetaUpdate,
  useEntityCreate,
  useEntityUpdate,
  useEntityDelete,
  useBlockWords,
  useUnblockWord,
  useToggleWordBlacklist,
  useRequireWord,
  useRemoveRequiredWord,
  useInboxAnalytics,
  useInboxBoardsArray,
  useEntityFolder,
  useEntityDuplicate,
  useExportToBoard,
  useCurrentEntity,
  useRemoveInboxAsset,
  usePublishedFoldersCount,
};

export default useEntity;
